import { useEffect, useRef, useState } from 'react';
import {
  Check,
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import { ConversationSummary } from '../types';

interface Props {
  conversations: ConversationSummary[];
  activeSessionId: string | null;
  authed: boolean;
  isLoading: boolean;
  /** The list fetch failed (network or server error). */
  isError?: boolean;
  /** The fetch failed with 401/403 — the session/token has lapsed. */
  authExpired?: boolean;
  /** Re-run the history fetch (wired to React Query's refetch). */
  onRetry?: () => void;
  onNewChat: () => void;
  onSelect: (sessionId: string) => void;
  onRename: (sessionId: string, title: string) => void;
  onDelete: (sessionId: string) => void;
}

// Compact relative time for the row subtitle ("2h", "3d", "Apr 5").
function relativeTime(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(then).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function ConversationRow({
  conversation,
  active,
  onSelect,
  onRename,
  onDelete,
}: {
  conversation: ConversationSummary;
  active: boolean;
  onSelect: (sessionId: string) => void;
  onRename: (sessionId: string, title: string) => void;
  onDelete: (sessionId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conversation.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startRename = () => {
    setDraft(conversation.title);
    setEditing(true);
    setMenuOpen(false);
  };

  const commitRename = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== conversation.title) {
      onRename(conversation.sessionId, next);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-2 py-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="flex-1 min-w-0 text-sm px-2 py-1 rounded-md border border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-400"
          maxLength={120}
        />
        <button
          type="button"
          onClick={commitRename}
          className="p-1 rounded text-green-600 hover:bg-green-50"
          aria-label="Save title"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="p-1 rounded text-neutral-500 hover:bg-neutral-100"
          aria-label="Cancel rename"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group relative flex items-center rounded-lg ${
        active ? 'bg-primary-50 text-primary-900' : 'hover:bg-neutral-100 text-neutral-800'
      }`}
    >
      <button
        type="button"
        onClick={() => onSelect(conversation.sessionId)}
        className="flex-1 min-w-0 text-left px-3 py-2"
        title={conversation.title}
      >
        <div className="text-sm font-medium truncate">{conversation.title}</div>
        <div className="text-[11px] text-neutral-500 flex items-center gap-1">
          <span>{relativeTime(conversation.updatedAt)}</span>
          {typeof conversation.messageCount === 'number' && (
            <>
              <span aria-hidden>·</span>
              <span>
                {conversation.messageCount}{' '}
                {conversation.messageCount === 1 ? 'msg' : 'msgs'}
              </span>
            </>
          )}
        </div>
      </button>

      <div className="relative flex-shrink-0 pr-1">
        <button
          type="button"
          onClick={() => {
            setMenuOpen((o) => !o);
            setConfirmDelete(false);
          }}
          className={`p-1 rounded text-neutral-500 hover:bg-neutral-200 ${
            menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
          }`}
          aria-label="Conversation actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>

        {menuOpen && (
          <>
            {/* click-away backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setMenuOpen(false);
                setConfirmDelete(false);
              }}
            />
            <div
              role="menu"
              className="absolute right-0 z-20 mt-1 w-40 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 text-sm"
            >
              <button
                type="button"
                role="menuitem"
                onClick={startRename}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-neutral-700 hover:bg-neutral-50"
              >
                <Pencil className="w-3.5 h-3.5" /> Rename
              </button>
              {confirmDelete ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(false);
                    onDelete(conversation.sessionId);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-red-700 bg-red-50 hover:bg-red-100 font-medium"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Confirm delete
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setConfirmDelete(true)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ConversationSidebar({
  conversations,
  activeSessionId,
  authed,
  isLoading,
  isError,
  authExpired,
  onRetry,
  onNewChat,
  onSelect,
  onRename,
  onDelete,
}: Props) {
  return (
    <aside className="h-full flex flex-col bg-white border-r border-neutral-200 w-[260px] xl:w-[280px]">
      <div className="flex-shrink-0 p-3">
        <Button variant="secondary" size="sm" onClick={onNewChat} className="w-full">
          <MessageSquarePlus className="w-4 h-4" /> New chat
        </Button>
      </div>

      <div className="px-3 pb-1">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          History
        </h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-0.5">
        {!authed ? (
          <p className="px-3 py-6 text-xs text-neutral-500 text-center">
            Sign in to save and revisit your conversations.
          </p>
        ) : isLoading ? (
          <div className="px-2 py-2 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-9 rounded-lg bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : conversations.length > 0 ? (
          // Always render whatever we have, even if a background refetch is
          // erroring — never blank an already-loaded list.
          conversations.map((c) => (
            <ConversationRow
              key={c.sessionId}
              conversation={c}
              active={c.sessionId === activeSessionId}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))
        ) : authExpired ? (
          // 401/403 with no cached list — the token lapsed. Prompt a sign-in
          // instead of the misleading "No conversations yet".
          <div className="px-3 py-6 text-center space-y-2">
            <p className="text-xs text-neutral-500">
              Your session expired. Sign in to see your saved conversations.
            </p>
            <a
              href="/login"
              className="inline-block text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Sign in
            </a>
          </div>
        ) : isError ? (
          // Network/server error with no cached list — offer a retry rather
          // than implying the user has no history.
          <div className="px-3 py-6 text-center space-y-2">
            <p className="text-xs text-neutral-500">Couldn&apos;t load your history.</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="inline-block text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Try again
              </button>
            )}
          </div>
        ) : (
          <p className="px-3 py-6 text-xs text-neutral-500 text-center">
            No conversations yet. Ask a question to start one.
          </p>
        )}
      </div>
    </aside>
  );
}
